import { Injectable, inject, DestroyRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, filter, takeUntil, timer, of, throwError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, retryWhen, delayWhen, tap, switchMap } from 'rxjs/operators';
import * as signalR from '@microsoft/signalr';
import { environment } from '@environments/environment';

export interface SignalRConnectionInfo {
    url: string;
    accessToken: string;
}

export interface SignalRMessage {
    type: string;
    payload: any;
}

/**
 * Base service for SignalR communication using Azure SignalR Service
 */
@Injectable({
    providedIn: 'root'
})
export class BaseSignalRService {
    private readonly http = inject(HttpClient);
    private readonly destroyRef = inject(DestroyRef);

    // Configuration
    private apiBaseUrl = environment.apiUrl + '/v1/SignalR';
    private hubName = 'chat';

    // Connection state tracking
    private hubConnection: signalR.HubConnection | null = null;
    private connectionId = '';
    private connectionEstablished$ = new BehaviorSubject<boolean>(false);
    private messagesReceived$ = new Subject<SignalRMessage>();
    private reconnecting$ = new BehaviorSubject<boolean>(false);
    private connectionAttempts = 0;
    private maxRetryAttempts = 5;
    private isConnecting = false;

    // Destructor subject for dynamic subscriptions
    private unsubscribe$ = new Subject<void>();

    // Exposed observables
    readonly connectionEstablished = this.connectionEstablished$.asObservable();
    readonly reconnecting = this.reconnecting$.asObservable();
    readonly connectionStatus = this.connectionEstablished$;

    constructor() {
        // Automatically close connection when service is destroyed
        this.destroyRef.onDestroy(() => {
            this.disconnect();
            this.unsubscribe$.next();
            this.unsubscribe$.complete();
        });
    }

    /**
     * Initialize the SignalR connection with the specified user ID
     * @param userId The user ID to connect as
     */
    async connect(userId: string): Promise<void> {
        if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
            console.log('Hub connection already exists and connected');
            return;
        }

        if (this.isConnecting) {
            console.log('Connection already in progress, waiting...');
            return;
        }

        this.isConnecting = true;
        this.connectionAttempts = 0;

        try {
            await this.attemptConnection(userId);
        } catch (error) {
            this.isConnecting = false;
            this.connectionEstablished$.next(false);
            throw error;
        }
    }

    /**
     * Attempt to establish connection with retry logic
     */
    private async attemptConnection(userId: string): Promise<void> {
        while (this.connectionAttempts < this.maxRetryAttempts) {
            try {
                this.connectionAttempts++;
                console.log(`Attempting SignalR connection (attempt ${this.connectionAttempts}/${this.maxRetryAttempts})`);

                // Get connection info from the server
                const connectionInfo = await this.getConnectionInfoWithRetry(userId);

                // Create the hub connection
                this.hubConnection = new signalR.HubConnectionBuilder()
                    .withUrl(connectionInfo.url, {
                        accessTokenFactory: () => connectionInfo.accessToken,
                        withCredentials: false,
                    })
                    .withAutomaticReconnect({
                        nextRetryDelayInMilliseconds: retryContext => {
                            // Implement exponential backoff with jitter
                            const maxDelay = 30000; // Max 30 seconds
                            const baseDelay = 1000; // Start at 1 second
                            let delay = Math.min(maxDelay, baseDelay * Math.pow(2, retryContext.previousRetryCount));

                            // Add jitter to prevent all clients reconnecting simultaneously
                            const jitter = delay * 0.2; // 20% jitter
                            delay = delay - jitter + (Math.random() * jitter * 2);

                            console.log(`SignalR reconnecting in ${delay}ms, attempt ${retryContext.previousRetryCount + 1}`);
                            return delay;
                        }
                    })
                    .configureLogging(signalR.LogLevel.Information)
                    .build();

                // Set up connection event handlers
                this.setupConnectionEvents();

                // Set up default message handlers
                this.setupDefaultMessageHandlers();

                // Start the connection
                await this.hubConnection.start();
                this.connectionId = this.hubConnection.connectionId ?? '';
                console.log(`SignalR connected with connection ID ${this.connectionId}`);
                this.connectionEstablished$.next(true);
                this.isConnecting = false;
                this.connectionAttempts = 0; // Reset attempts on successful connection
                return;

            } catch (error: any) {
                console.error(`Connection attempt ${this.connectionAttempts} failed:`, error);

                // Clean up failed connection
                if (this.hubConnection) {
                    try {
                        await this.hubConnection.stop();
                    } catch (stopError) {
                        console.error('Error stopping failed connection:', stopError);
                    }
                    this.hubConnection = null;
                }

                // Check if it's a 429 error (rate limit)
                if (error.message?.includes('429') || error.message?.includes('Connection count reaches limit')) {
                    console.log('Rate limit reached, waiting before retry...');
                    const waitTime = Math.min(5000 * Math.pow(2, this.connectionAttempts - 1), 30000); // Exponential backoff, max 30s
                    await new Promise(resolve => setTimeout(resolve, waitTime));
                } else if (this.connectionAttempts < this.maxRetryAttempts) {
                    // For other errors, wait a bit before retry
                    const waitTime = Math.min(2000 * this.connectionAttempts, 10000);
                    await new Promise(resolve => setTimeout(resolve, waitTime));
                } else {
                    // Max attempts reached
                    this.isConnecting = false;
                    throw new Error(`Failed to connect after ${this.maxRetryAttempts} attempts: ${error.message}`);
                }
            }
        }
    }

    /**
     * Get connection info with retry logic for 429 errors
     */
    private async getConnectionInfoWithRetry(userId: string): Promise<SignalRConnectionInfo> {
        return new Promise((resolve, reject) => {
            this.http.get<SignalRConnectionInfo>(`${this.apiBaseUrl}/negotiate/${userId}`)
                .pipe(
                    retryWhen(errors =>
                        errors.pipe(
                            switchMap((error: any) => {
                                if (error.status === 429 || error.message?.includes('429')) {
                                    console.log('Rate limit hit during negotiation, retrying...');
                                    // Exponential backoff for 429 errors
                                    const delay = Math.min(2000 * Math.pow(2, this.connectionAttempts - 1), 15000);
                                    return timer(delay);
                                }
                                return throwError(() => error);
                            }),
                            takeUntil(this.unsubscribe$)
                        )
                    ),
                    catchError(error => {
                        console.error('Failed to get connection info after retries:', error);
                        return throwError(() => error);
                    })
                )
                .subscribe({
                    next: (connectionInfo) => resolve(connectionInfo),
                    error: (error) => reject(error)
                });
        });
    }

    /**
     * Disconnect from the SignalR hub
     */
    async disconnect(): Promise<void> {
        this.isConnecting = false;

        if (!this.hubConnection) {
            return;
        }

        try {
            await this.hubConnection.stop();
            console.log('SignalR disconnected');
        } catch (error) {
            console.error('Error disconnecting from SignalR:', error);
        } finally {
            this.hubConnection = null;
            this.connectionId = '';
            this.connectionEstablished$.next(false);
        }
    }

    /**
     * Get the connection info from the server (legacy method)
     */
    private getConnectionInfo(userId: string): Promise<SignalRConnectionInfo> {
        return this.http.get<SignalRConnectionInfo>(`${this.apiBaseUrl}/negotiate/${userId}`).toPromise() as Promise<SignalRConnectionInfo>;
    }

    /**
     * Set up connection event handlers
     */
    private setupConnectionEvents(): void {
        if (!this.hubConnection) return;

        this.hubConnection.onreconnecting(error => {
            console.log('SignalR reconnecting:', error);
            this.reconnecting$.next(true);
            this.connectionEstablished$.next(false);
        });

        this.hubConnection.onreconnected(connectionId => {
            console.log(`SignalR reconnected with connection ID ${connectionId}`);
            this.connectionId = connectionId ?? '';
            this.reconnecting$.next(false);
            this.connectionEstablished$.next(true);
        });

        this.hubConnection.onclose(error => {
            console.log('SignalR connection closed:', error);
            this.reconnecting$.next(false);
            this.connectionEstablished$.next(false);

            // If connection was closed due to rate limiting, try to reconnect after a delay
            if (error?.message?.includes('429') || error?.message?.includes('Connection count reaches limit')) {
                console.log('Connection closed due to rate limiting, will attempt reconnection...');
                setTimeout(() => {
                    if (!this.isConnecting && this.connectionEstablished$.value === false) {
                        // Only attempt reconnection if we're not already connecting and not connected
                        this.connect(this.connectionId || 'unknown').catch(err =>
                            console.error('Failed to reconnect after rate limit:', err)
                        );
                    }
                }, 10000); // Wait 10 seconds before attempting reconnection
            }
        });
    }

    /**
     * Set up default message handlers
     */
    private setupDefaultMessageHandlers(): void {
        if (!this.hubConnection) return;

        // ReceiveMessage - standard broadcast message
        this.hubConnection.on('ReceiveMessage', (message: any) => {
            this.messagesReceived$.next({
                type: 'broadcastMessage',
                payload: message
            });
        });

        // ReceiveMessage - standard broadcast message
        this.hubConnection.on('CreateJob', (message: any) => {
            this.messagesReceived$.next({
                type: 'createJobMessage',
                payload: message
            });
        });

        this.hubConnection.on('CompleteStepJobAsync', (message: any) => {
            this.messagesReceived$.next({
                type: 'completeMessage',
                payload: message
            });
        });

        this.hubConnection.on('JobChangeStatus', (message: any) => {
            this.messagesReceived$.next({
                type: 'JobChangeStatus',
                payload: message
            });
        });

        this.hubConnection.on('JobTransfer', (message: any) => {
            this.messagesReceived$.next({
                type: 'JobTransfer',
                payload: message
            });
        });

        this.hubConnection.on('FinishJob', (message: any) => {
            this.messagesReceived$.next({
                type: 'FinishJob',
                payload: message
            });
        });

        // JobPriorityUpdated - Job priority update notification
        this.hubConnection.on('JobPriorityUpdated', (message: any) => {
            this.messagesReceived$.next({
                type: 'JobPriorityUpdated',
                payload: message
            });
        });

        // DispatchAlert - Dispatch alert notification
        this.hubConnection.on('DispatchAlert', (message: any) => {
            this.messagesReceived$.next({
                type: 'DispatchAlert',
                payload: message
            });
        });

        // ExcelImportCompleted - Excel import completion notification
        this.hubConnection.on('ExcelImportCompleted', (message: any) => {
            this.messagesReceived$.next({
                type: 'ExcelImportCompleted',
                payload: message
            });
        });

        // ReceivePrivateMessage - private message between users
        this.hubConnection.on('ReceivePrivateMessage', (message: any) => {
            this.messagesReceived$.next({
                type: 'privateMessage',
                payload: message
            });
        });

        // UserConnected - when a user connects
        this.hubConnection.on('UserConnected', (userId: string) => {
            this.messagesReceived$.next({
                type: 'userConnected',
                payload: { userId }
            });
        });

        // UserDisconnected - when a user disconnects
        this.hubConnection.on('UserDisconnected', (userId: string) => {
            this.messagesReceived$.next({
                type: 'userDisconnected',
                payload: { userId }
            });
        });

        // ConnectedUsers - list of connected users
        this.hubConnection.on('ConnectedUsers', (users: string[]) => {
            this.messagesReceived$.next({
                type: 'connectedUsers',
                payload: { users }
            });
        });
    }

    /**
     * Add a custom message handler
     * @param methodName The name of the method to handle
     * @param handler The handler function
     */
    addMessageHandler(methodName: string, handler: (data: any) => void): void {
        if (!this.hubConnection) {
            console.error('Cannot add message handler: hub connection not established');
            return;
        }

        this.hubConnection.on(methodName, handler);
    }

    /**
     * Remove a custom message handler
     * @param methodName The name of the method to remove the handler for
     */
    removeMessageHandler(methodName: string): void {
        if (!this.hubConnection) return;
        this.hubConnection.off(methodName);
    }

    /**
     * Listen for messages of a specific type
     * @param messageType The type of message to listen for
     * @returns An observable that emits messages of the specified type
     */
    listenFor<T = any>(messageType: string): Observable<T> {
        return this.messagesReceived$.pipe(
            filter(message => message.type === messageType),
            map(message => message.payload as T),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    /**
     * Send a message to all connected clients
     * @param message The message to send
     */
    async sendMessage(message: string): Promise<void> {
        if (!this.ensureConnection()) return;
        await this.hubConnection!.invoke('SendMessage', message);
    }

    /**
     * Send a message to a specific user
     * @param userId The ID of the user to send the message to
     * @param message The message to send
     */
    async sendPrivateMessage(userId: string, message: string): Promise<void> {
        if (!this.ensureConnection()) return;
        await this.hubConnection!.invoke('SendPrivateMessage', userId, message);
    }

    /**
     * Ensure that the connection is established
     * @returns True if the connection is established, false otherwise
     */
    private ensureConnection(): boolean {
        if (!this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Connected) {
            console.error('Cannot send message: hub connection not established');
            return false;
        }
        return true;
    }

    /**
     * Get current connection state
     */
    getConnectionState(): signalR.HubConnectionState | null {
        return this.hubConnection?.state || null;
    }

    /**
     * Check if connection is healthy
     */
    isConnectionHealthy(): boolean {
        return this.hubConnection?.state === signalR.HubConnectionState.Connected;
    }
}

// Helper function for mapping operations
function map<T, R>(fn: (value: T) => R) {
    return (source: Observable<T>): Observable<R> => {
        return new Observable<R>(subscriber => {
            return source.subscribe({
                next: value => {
                    try {
                        subscriber.next(fn(value));
                    } catch (error) {
                        subscriber.error(error);
                    }
                },
                error: error => subscriber.error(error),
                complete: () => subscriber.complete()
            });
        });
    };
}
